import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, Send, Search, Rocket, LifeBuoy, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Label, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";

const ICONS = {
  setup_nudge: LifeBuoy,
  top_performers: Trophy,
  personal_rank: Trophy,
  new_features: Rocket,
};
const ACCENT = {
  setup_nudge: "text-amber-600 bg-amber-50",
  top_performers: "text-emerald-600 bg-emerald-50",
  personal_rank: "text-indigo-600 bg-indigo-50",
  new_features: "text-sky-600 bg-sky-50",
};

export default function PlatformEmails() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(null); // active template id

  useEffect(() => {
    api.get("/platform/campaigns/templates")
      .then((r) => setTemplates(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <div className="space-y-6" data-testid="platform-emails">
      <div>
        <Label>Platform</Label>
        <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Email campaigns</h1>
        <p className="mt-1 text-sm text-slate-500">
          Send on-demand emails to organisation owners or employees. Each send goes through your configured provider (SES → TurboSMTP failover) and is logged.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => {
          const Icon = ICONS[t.id] || Mail;
          return (
            <button key={t.id} data-testid={`template-${t.id}`}
                    onClick={() => setOpen(t.id)}
                    className="rounded-xl border border-slate-200 bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-indigo-300">
              <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${ACCENT[t.id] || "bg-slate-50 text-slate-500"}`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="mt-3 font-head text-lg font-semibold text-slate-900">{t.name}</p>
              <p className="mt-1 text-sm text-slate-500">{t.description}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-indigo-600">Open campaign →</p>
            </button>
          );
        })}
      </div>

      <CampaignSheet templateId={open} onClose={() => setOpen(null)}
                     templates={templates} />
    </div>
  );
}

function CampaignSheet({ templateId, onClose, templates }) {
  const [audience, setAudience] = useState({ count: 0, recipients: [] });
  const [picked, setPicked] = useState(() => new Set());
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [extra, setExtra] = useState({ title: "", body_md: "" });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const template = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);

  const load = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/campaigns/audience?template=${templateId}`);
      setAudience(data);
      setPicked(new Set(data.recipients.map((r) => r.id)));
      if (!template?.needs_extra) {
        try {
          const { data: p } = await api.post("/platform/campaigns/preview", { template: templateId, extra: {} });
          setPreview(p);
        } catch (e) {
          setPreview(null);
          if (data.recipients.length) toast.error(apiError(e));
        }
      } else {
        setPreview(null);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [templateId, template]);

  useEffect(() => {
    if (!templateId) { setPreview(null); setExtra({ title: "", body_md: "" }); setPicked(new Set()); return; }
    load();
  }, [templateId, load]);

  const refreshPreview = async (overrideRecipient) => {
    try {
      const body = { template: templateId, extra, recipient_id: overrideRecipient };
      const { data } = await api.post("/platform/campaigns/preview", body);
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleAll = (on) => setPicked(new Set(on ? audience.recipients.map((r) => r.id) : []));
  const togglePick = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const send = async () => {
    if (picked.size === 0) { toast.error("Pick at least one recipient."); return; }
    if (!window.confirm(`Send "${template?.name}" to ${picked.size} recipient${picked.size === 1 ? "" : "s"} now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/platform/campaigns/send", {
        template: templateId, recipient_ids: Array.from(picked), extra,
      });
      if (data.failed?.length) {
        toast.warning(`Sent ${data.sent} · ${data.failed.length} failed`);
      } else {
        toast.success(`Sent ${data.sent} email${data.sent === 1 ? "" : "s"}`);
      }
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return audience.recipients;
    return audience.recipients.filter((r) =>
      [r.name, r.email, r.org_name, r.location_name].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)));
  }, [audience, filter]);

  const needsExtra = template?.needs_extra;

  return (
    <Dialog open={!!templateId} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {template?.name || "Campaign"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid min-h-[460px] grid-cols-1 lg:grid-cols-5">
          {/* Audience + extra */}
          <div className="flex flex-col overflow-hidden border-r border-slate-100 lg:col-span-2">
            {needsExtra && (
              <div className="space-y-2 border-b border-slate-100 p-4">
                <Label>Announcement</Label>
                <Input value={extra.title} onChange={(e) => setExtra({ ...extra, title: e.target.value })}
                       placeholder="Subject line (e.g. New dashboard features)" data-testid="extra-title" />
                <Textarea rows={5} value={extra.body_md}
                          onChange={(e) => setExtra({ ...extra, body_md: e.target.value })}
                          placeholder="Describe the update. Use lines starting with - for bullet points."
                          data-testid="extra-body" />
                <Button variant="outline" size="sm" onClick={() => refreshPreview()}
                        data-testid="refresh-preview">Update preview</Button>
              </div>
            )}
            <div className="flex items-center gap-2 border-b border-slate-100 p-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter recipients" className="pl-9" />
              </div>
              <button onClick={() => toggleAll(picked.size !== audience.count)} data-testid="toggle-all"
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:border-indigo-300 hover:text-indigo-700">
                {picked.size === audience.count ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto sv-scroll" data-testid="recipient-list">
              {loading ? (
                <p className="p-6 text-sm text-slate-400">Loading audience…</p>
              ) : audience.recipients.length === 0 ? (
                <EmptyState icon={Mail} title="No recipients" hint="This template has no one to send to right now." />
              ) : visible.map((r) => (
                <label key={r.id} className="flex cursor-pointer items-center gap-3 border-b border-slate-50 px-4 py-2 text-sm hover:bg-indigo-50/40"
                       data-testid={`recipient-${r.id}`}>
                  <Checkbox checked={picked.has(r.id)} onCheckedChange={() => togglePick(r.id)} />
                  <div className="min-w-0 flex-1" onClick={() => refreshPreview(r.id)}>
                    <p className="truncate font-medium text-slate-800">{r.name}</p>
                    <p className="truncate text-xs text-slate-500">
                      {r.email}
                      {r.org_name ? <span className="text-slate-400"> · {r.org_name}</span> : null}
                      {r.location_name ? <span className="text-slate-400"> · {r.location_name}</span> : null}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500" data-testid="audience-count">
              {picked.size} / {audience.count} selected
            </div>
          </div>

          {/* Preview */}
          <div className="flex flex-col overflow-hidden lg:col-span-3">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
              <span>Preview {preview?.recipient ? <span className="text-slate-400">for {preview.recipient.email}</span> : null}</span>
              {preview?.subject && <span className="font-semibold text-slate-700">Subject: {preview.subject}</span>}
            </div>
            <div className="flex-1 overflow-y-auto sv-scroll bg-slate-50 p-4">
              {preview?.html ? (
                <iframe title="Email preview" srcDoc={preview.html} className="h-[480px] w-full rounded-lg border border-slate-200 bg-white"
                        data-testid="preview-iframe" />
              ) : needsExtra ? (
                <p className="p-6 text-center text-sm text-slate-400">Type a subject and body on the left, then tap "Update preview".</p>
              ) : loading ? (
                <p className="p-6 text-center text-sm text-slate-400">Rendering…</p>
              ) : (
                <p className="p-6 text-center text-sm text-slate-400">Pick a recipient on the left to preview their email.</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-100 px-6 py-3">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || picked.size === 0}
                  className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="send-campaign">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send to {picked.size || 0}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
